import { Component, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlignLeft, ArrowDown, ArrowUp, Box, Boxes, ChartSpline, CircleHelp, Eye, ImageIcon, Layers, Plus, Save, Send, Shapes, Sigma, Smartphone, Sparkles, Trash2, TriangleAlert, Undo2, Unlink, Video } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { CardFeed, LessonPlayer, lessonCards } from "@jange/lesson-player";
import { feedLint } from "@jange/shared";
import { lazyThree } from "@jange/lesson-player/three-lazy";
import type { LessonContent } from "@jange/shared";
import { BankBrowser } from "./BankBrowser";
import { api, can, type Asset, type BankItem, type Block, type ContentBody, type NotionDetail } from "../lib/api";
import { useStudio } from "../lib/context";
import { BLOCK_TYPES, stepSliders, emptyBody, localDraft, move, newBlock, nextId, removeBlock, updateStep, validateBody, type EditableType } from "../lib/content";
import { Badge, Drawer, Empty, errorMessage, Field, fmtDate, Seg } from "./ui";
import { FigureEditor } from "./FigureEditor";
import { GeometryEditor } from "./GeometryEditor";
import { ImageEditor } from "./ImageEditor";
import { ModelEditor, SolidEditor } from "./Scene3DEditors";

/** Comparaison indépendante de l'ordre des clés (le JSONB de Postgres réordonne les clés). */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stable((v as Record<string, unknown>)[k])}`).join(",")}}`;
  return JSON.stringify(v);
}
const clone = <T,>(x: T): T => structuredClone(x);
const snippet = (b: Block, n = 40) => {
  const s = b.type === "text" || b.type === "mistake" ? b.md : b.type === "quiz" ? `? ${b.question}` : b.type === "formula" ? b.latex : b.type === "figure" ? "figure" : b.type === "geometry" ? "géométrie" : b.type === "image" ? "image" : b.type === "video" ? "vidéo" : b.type;
  return s.length > n ? `${s.slice(0, n)}…` : s || "(vide)";
};
const ICON: Record<string, ReactNode> = {
  text: <AlignLeft size={14} />, formula: <Sigma size={14} />, figure: <ChartSpline size={14} />, geometry: <Shapes size={14} />, image: <ImageIcon size={14} />, mistake: <TriangleAlert size={14} />,
  video: <Video size={14} />, solid3d: <Box size={14} />, scene3d: <Boxes size={14} />, quiz: <CircleHelp size={14} />,
};
/** L'ordre d'une étape suit toujours l'ordre général des blocs (même rendu en pas à pas et en défilement). */
const sortSteps = (b: ContentBody): ContentBody => {
  const pos = new Map(b.blocks.map((x, i) => [x.id, i]));
  return { ...b, steps: b.steps.map((s) => ({ ...s, blockIds: [...s.blockIds].sort((x, y) => (pos.get(x) ?? 1e9) - (pos.get(y) ?? 1e9)) })) };
};
const PREVIEW_MQ = "(min-width: 1241px)";
function useWide() {
  const [wide, setWide] = useState(() => typeof window === "undefined" || window.matchMedia(PREVIEW_MQ).matches);
  useEffect(() => { const m = window.matchMedia(PREVIEW_MQ); const f = () => setWide(m.matches); m.addEventListener("change", f); return () => m.removeEventListener("change", f); }, []);
  return wide;
}

type Draft = { id: string; status: "PENDING" | "READY" | "FAILED"; warnings: string[]; error: string | null };
/**
 * Autre contenu que la leçon d'une notion, écrit dans le même éditeur (corrigé d'une question d'annales) : ses propres
 * routes d'enregistrement, de soumission, de relecture et de brouillon IA. `notion` porte alors l'identifiant du
 * contenu (brouillon local), son titre et ses versions.
 */
export type EditorOps = {
  save: (lang: string, body: ContentBody, submit: boolean) => Promise<{ id: string; version: number }>;
  submit: (id: string) => Promise<unknown>;
  review: (id: string, decision: "VALIDATED" | "REJECTED", note?: string) => Promise<unknown>;
  refresh: () => Promise<void>;
  ai?: { start: (lang: string, instructions?: string) => Promise<Draft>; get: (id: string) => Promise<Draft>; description: string };
};

export function ContentEditor({ notion, lang, reviewId, insertModel, onInserted, ops }: { notion: NotionDetail; lang: string; reviewId: string | null; insertModel?: string | null; onInserted?: () => void; ops?: EditorOps }) {
  const { teacher, toast, countryId } = useStudio();
  const qc = useQueryClient();
  const versions = useMemo(() => notion.contents.filter((c) => c.lang === lang).sort((a, b) => b.version - a.version), [notion.contents, lang]);
  const [selectedId, setSelectedId] = useState<string | null>(() => (reviewId && versions.some((v) => v.id === reviewId) ? reviewId : versions[0]?.id ?? null));
  const selected = versions.find((v) => v.id === selectedId) ?? null;
  const base = useMemo<ContentBody>(() => (selected ? selected.body : emptyBody()), [selected]);
  const [body, setBody] = useState<ContentBody>(() => clone(base));
  const [previewStep, setPreviewStep] = useState(0);
  const [view, setView] = useState<"steps" | "blocks">("steps");
  const [previewOpen, setPreviewOpen] = useState(false);
  const wide = useWide();
  const dirty = stable(body) !== stable(base);
  const canWrite = can.author(teacher);
  const errors = validateBody(body, lang);
  // Vidéos prêtes de la médiathèque : choix dans les blocs vidéo et lecture dans l'aperçu.
  const videos = useQuery({ queryKey: ["assets", "VIDEO", "ready"], queryFn: () => api.assets({ kind: "VIDEO", limit: 100 }), staleTime: 60_000 });
  const models = useQuery({ queryKey: ["assets", "SCENE_3D", "ready"], queryFn: () => api.assets({ kind: "SCENE_3D", limit: 100 }), staleTime: 60_000 });
  const images = useQuery({ queryKey: ["assets", "IMAGE", "ready"], queryFn: () => api.assets({ kind: "IMAGE", limit: 100 }), staleTime: 60_000 });
  const audios = useQuery({ queryKey: ["assets", "AUDIO", "ready"], queryFn: () => api.assets({ kind: "AUDIO", limit: 100 }), staleTime: 60_000 });
  const readyAudios = useMemo(() => (audios.data?.items ?? []).filter((a) => a.status === "READY" && (!a.languageCode || a.languageCode === lang)), [audios.data, lang]);
  const [previewMode, setPreviewMode] = useState<"STEPS" | "FEED">("STEPS");
  // Conseils du mode défilement et de la révision : propres aux leçons (un corrigé ne revient pas en révision).
  const lint = useMemo(() => (ops ? [] : feedLint(body)), [body, ops]);
  const readyVideos = useMemo(() => (videos.data?.items ?? []).filter((a) => a.status === "READY"), [videos.data]);
  const readyModels = useMemo(() => (models.data?.items ?? []).filter((a) => a.status === "READY"), [models.data]);
  const readyImages = useMemo(() => (images.data?.items ?? []).filter((a) => a.status === "READY"), [images.data]);
  const resolveAsset = useMemo(() => { const m = new Map([...readyVideos, ...readyModels, ...readyImages, ...readyAudios].map((a) => [a.id, a.url])); return (id: string) => m.get(id) ?? null; }, [readyVideos, readyModels, readyImages, readyAudios]);

  // Brouillon local retrouvé (onglet fermé, coupure réseau, plantage)
  const [restore, setRestore] = useState(() => {
    const d = localDraft.get(notion.id, lang);
    return d && stable(d.body) !== stable(base) ? d : null;
  });

  useEffect(() => {
    if (!dirty || !canWrite) return;
    const t = setTimeout(() => localDraft.set(notion.id, lang, { body, baseId: selectedId, savedAt: Date.now() }), 600);
    return () => clearTimeout(t);
  }, [body, dirty, canWrite, notion.id, lang, selectedId]);

  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const refresh = async () => {
    if (ops) { await ops.refresh(); return; }
    await qc.refetchQueries({ queryKey: ["notion", notion.id] });
    void qc.invalidateQueries({ queryKey: ["curricula", countryId] });
    void qc.invalidateQueries({ queryKey: ["dashboard", countryId] });
  };

  const save = useMutation({
    mutationFn: async (submit: boolean) => { const r = await (ops ? ops.save(lang, body, submit) : api.saveContent(notion.id, lang, body, submit)); await refresh(); return r; },
    onSuccess: (r, submit) => { localDraft.clear(notion.id, lang); setRestore(null); setSelectedId(r.id); toast(submit ? `Version ${r.version} soumise en relecture` : `Brouillon enregistré (version ${r.version})`); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const submitExisting = useMutation({
    mutationFn: async (id: string) => { await (ops ? ops.submit(id) : api.submitContent(id)); await refresh(); },
    onSuccess: () => toast("Soumis en relecture"),
    onError: (e) => toast(errorMessage(e), "err"),
  });

  const selectVersion = (id: string) => {
    if (id === selectedId) return;
    if (dirty && !window.confirm("Vos modifications non enregistrées seront perdues. Continuer ?")) return;
    const v = versions.find((x) => x.id === id);
    setSelectedId(id);
    setBody(clone(v ? v.body : emptyBody()));
    setPreviewStep(0);
  };

  const frSource = lang !== "fr" ? notion.contents.filter((c) => c.lang === "fr").sort((a, b) => (a.status === "VALIDATED" ? -1 : 0) - (b.status === "VALIDATED" ? -1 : 0) || b.version - a.version)[0] : undefined;

  const lesson = useMemo<LessonContent & { title: string }>(() => ({
    notionId: notion.id, lang, script: "Latn", orthography: null, version: selected?.version ?? 0, validationStatus: "IN_REVIEW",
    blocks: body.blocks, steps: body.steps, title: notion.title,
  }), [body, lang, notion.id, notion.title, selected?.version]);
  const stepIdx = Math.min(previewStep, Math.max(0, body.steps.length - 1));
  const step = body.steps[stepIdx];

  const [picking, setPicking] = useState(false);
  const setBlockById = (id: string, b: Block) => setBody((x) => ({ ...x, blocks: x.blocks.map((y) => (y.id === id ? b : y)) }));
  const addBlock = (type: EditableType, patch?: Partial<Block>) => setBody((x) => {
    if (x.blocks.length >= 60) return x;
    const b = { ...newBlock(type, nextId("b", x.blocks.map((y) => y.id))), ...patch } as Block;
    // Le nouveau bloc apparaît dans l'étape ouverte : c'est presque toujours ce qu'on veut.
    const steps = x.steps.map((s, k) => (k === stepIdx ? { ...s, blockIds: [...s.blockIds, b.id] } : s));
    return { blocks: [...x.blocks, b], steps };
  });
  const addStep = () => setBody((x) => {
    if (x.steps.length >= 30) return x;
    const prev = x.steps[x.steps.length - 1];
    const s = { id: nextId("s", x.steps.map((y) => y.id)), title: { [lang]: "" }, blockIds: prev ? [...prev.blockIds] : [], ...(prev?.figureParams ? { figureParams: { ...prev.figureParams } } : {}) };
    setPreviewStep(x.steps.length);
    setView("steps");
    return { ...x, steps: [...x.steps, s] };
  });
  /** Un modèle de la banque devient un bloc 3D (crédit et poids recopiés par le serveur à l'enregistrement). */
  const modelPatch = (m: BankItem) => ({ assetId: m.id, sizeBytes: m.sizeBytes, credit: m.credit }) as Partial<Block>;
  const onAddType = (type: EditableType) => (type === "scene3d" ? setPicking(true) : addBlock(type));

  // Arrivée depuis la banque (« Ajouter à une leçon ») : le modèle est posé sur une nouvelle carte à la fin.
  const inserted = useRef(false);
  useEffect(() => {
    if (!insertModel || inserted.current || !canWrite) return;
    inserted.current = true;
    api.bankItem(insertModel).then((m) => {
      setBody((x) => {
        if (x.blocks.length >= 60 || x.steps.length >= 30) return x;
        const b = { ...newBlock("scene3d", nextId("b", x.blocks.map((y) => y.id))), ...modelPatch(m) } as Block;
        const s = { id: nextId("s", x.steps.map((y) => y.id)), title: { [lang]: m.title }, blockIds: [b.id] };
        setPreviewStep(x.steps.length);
        return { blocks: [...x.blocks, b], steps: [...x.steps, s] };
      });
      setView("steps");
      toast(`« ${m.title} » ajouté sur une nouvelle carte : enregistrez le brouillon pour le garder`);
    }).catch((e) => toast(errorMessage(e), "err")).finally(() => onInserted?.());
  }, [insertModel, canWrite]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveStep = (i: number, d: -1 | 1) => { setBody((x) => ({ ...x, steps: move(x.steps, i, d) })); setPreviewStep(i + d); };
  const removeStep = (i: number) => {
    if (!window.confirm(`Supprimer l'étape ${i + 1} ? Ses blocs restent disponibles pour les autres étapes.`)) return;
    setBody((x) => ({ ...x, steps: x.steps.filter((_, k) => k !== i) }));
    setPreviewStep(Math.max(0, i - 1));
  };
  /** Déplace un bloc d'un cran dans l'étape : échange avec le bloc voisin de cette étape. */
  const moveInStep = (id: string, d: -1 | 1) => setBody((x) => {
    const s = x.steps[stepIdx]; if (!s) return x;
    const ordered = x.blocks.filter((b) => s.blockIds.includes(b.id)).map((b) => b.id);
    const j = ordered.indexOf(id) + d; if (j < 0 || j >= ordered.length) return x;
    const a = x.blocks.findIndex((b) => b.id === id), c = x.blocks.findIndex((b) => b.id === ordered[j]);
    const blocks = x.blocks.slice(); [blocks[a], blocks[c]] = [blocks[c]!, blocks[a]!];
    return sortSteps({ ...x, blocks });
  });
  const toggleInStep = (id: string, on: boolean) => setBody((x) => sortSteps(updateStep(x, stepIdx, { blockIds: on ? [...(x.steps[stepIdx]?.blockIds ?? []), id] : (x.steps[stepIdx]?.blockIds ?? []).filter((y) => y !== id) })));
  const removeEverywhere = (b: Block) => { if (window.confirm(`Supprimer le bloc ${b.id} ? Il sera retiré de toutes les étapes.`)) setBody((x) => removeBlock(x, b.id)); };

  const usedIn = useMemo(() => {
    const m = new Map<string, number[]>();
    body.steps.forEach((s, i) => s.blockIds.forEach((id) => m.set(id, [...(m.get(id) ?? []), i + 1])));
    return m;
  }, [body.steps]);
  const orphans = body.blocks.filter((b) => !usedIn.has(b.id));

  const isMine = selected?.authorId === teacher?.id;
  const nextVersion = (versions[0]?.version ?? 0) + 1;
  const stepBlocks = step ? body.blocks.filter((b) => step.blockIds.includes(b.id)) : [];
  const editorProps = { lang, videos: readyVideos, models: readyModels, images: readyImages };

  const preview = (
    <>
      <div className="preview-head">
        <Smartphone size={16} color="var(--text-3)" /><h3>Aperçu élève</h3><span className="spacer" />
        <div className="seg" role="radiogroup" aria-label="Mode de l'aperçu">
          {(["STEPS", "FEED"] as const).map((m) => <button key={m} type="button" role="radio" aria-checked={previewMode === m} onClick={() => setPreviewMode(m)}>{m === "STEPS" ? "Pas à pas" : "Défilement"}</button>)}
        </div>
      </div>
      <div className="phone">
        <div className="bar"><div className="kicker" style={{ marginBottom: 2 }}>{lang.toUpperCase()}{previewMode === "STEPS" && body.steps.length ? ` · carte ${stepIdx + 1} / ${body.steps.length}` : ""}</div><div style={{ fontFamily: "var(--display)", fontWeight: 700 }}>{notion.title}</div></div>
        <div className="scroll" style={previewMode === "FEED" ? { overflow: "hidden" } : undefined}>
          {body.steps.length === 0
            ? <div className="empty">Ajoutez une étape pour voir l'aperçu.</div>
            : previewMode === "FEED"
              ? <PreviewBoundary resetKey={stable(body)}><div style={{ height: "100%" }}><CardFeed key={stable(body)} cards={lessonCards(lesson)} variant="lesson" title={notion.title} lang={lang} onEvent={() => {}} resolveAsset={resolveAsset} autoLoad3d three={lazyThree} /></div></PreviewBoundary>
              : <PreviewBoundary resetKey={stable(body)}><LessonPlayer lesson={lesson} lang={lang} stepIndex={stepIdx} onEvent={async () => {}} resolveAsset={resolveAsset} autoLoad3d three={lazyThree} /></PreviewBoundary>}
        </div>
      </div>
      {previewMode === "STEPS" && body.steps.length > 1 && (
        <div className="row" style={{ gap: 6 }}>
          <button type="button" className="icon-btn bordered" aria-label="Carte précédente" disabled={stepIdx === 0} onClick={() => setPreviewStep(stepIdx - 1)}><ArrowUp size={15} style={{ transform: "rotate(-90deg)" }} /></button>
          <span className="small muted strong">{stepIdx + 1} / {body.steps.length}</span>
          <button type="button" className="icon-btn bordered" aria-label="Carte suivante" disabled={stepIdx >= body.steps.length - 1} onClick={() => setPreviewStep(stepIdx + 1)}><ArrowDown size={15} style={{ transform: "rotate(-90deg)" }} /></button>
        </div>
      )}
    </>
  );

  return (
    <div className="stack" style={{ gap: 14 }}>
      {restore && (
        <div className="alert warn appear">
          <span style={{ flex: 1 }}>Un brouillon non enregistré du {fmtDate(new Date(restore.savedAt).toISOString())} a été retrouvé sur ce poste.</span>
          <button className="btn sm amber" onClick={() => { setBody(clone(restore.body)); setRestore(null); }}>Restaurer</button>
          <button className="btn sm ghost" onClick={() => { localDraft.clear(notion.id, lang); setRestore(null); }}>Ignorer</button>
        </div>
      )}
      {selected?.status === "REJECTED" && selected.reviewNote && <div className="alert err"><TriangleAlert size={16} /><span><b>Corrections demandées</b>{selected.validatedBy ? ` par ${selected.validatedBy}` : ""} : {selected.reviewNote}</span></div>}
      {selected?.status === "IN_REVIEW" && isMine && <div className="alert warn">Cette version attend la relecture d'un autre enseignant.</div>}
      {selected && can.reviewLang(teacher, lang) && selected.status === "IN_REVIEW" && !isMine && (
        <ReviewPanel contentId={selected.id} disabled={dirty} onDone={refresh} review={ops?.review} />
      )}
      {versions.length === 0 && canWrite && stable(body) === stable(emptyBody()) && (ops
        ? ops.ai && <AiLessonCard lang={lang} onDone={refresh} start={ops.ai.start} get={ops.ai.get} description={ops.ai.description} />
        : <AiLessonCard lang={lang} onDone={refresh} start={(l, instructions) => api.requestLessonDraft(notion.id, { languageCode: l, instructions })} get={api.lessonDraft}
          description="Leçon complète au format du lecteur (cartes, formules, figure, question, erreur fréquente, narration), à partir du titre et du programme officiel s'il a été fourni. Vous la relisez et la corrigez avant de la soumettre." />)}
      {versions.length === 0 && frSource && canWrite && stable(body) === stable(emptyBody()) && (
        <div className="card row wrap appear" style={{ borderColor: "var(--accent-line)" }}>
          <span className="icon-tile"><Layers size={18} /></span>
          <span style={{ flex: 1, minWidth: 240 }}><b>Aucune version dans cette langue.</b><br /><span className="small muted">Partez de la version française (v{frSource.version}) pour la traduire bloc par bloc, en gardant les mêmes figures.</span></span>
          <button className="btn" onClick={() => setBody(clone(frSource.body))}>Partir du français</button>
        </div>
      )}

      <div className="row wrap" style={{ gap: 10 }}>
        <VersionBar versions={versions} selectedId={selectedId} onSelect={selectVersion} />
        <span className="spacer" />
        <Seg label="Affichage de l'éditeur" value={view} onChange={setView} options={[{ value: "steps", label: <><Layers size={14} />Par étape</> }, { value: "blocks", label: <><AlignLeft size={14} />Tous les blocs</>, count: body.blocks.length }]} />
        {!wide && <button type="button" className="btn sm ghost" onClick={() => setPreviewOpen(true)}><Eye size={15} />Aperçu</button>}
      </div>

      <div className={`lesson ${wide ? "" : "no-preview"}`}>
        <nav className="outline" aria-label="Plan de la leçon">
          <div className="outline-title"><span>Plan · {body.steps.length} carte{body.steps.length > 1 ? "s" : ""}</span></div>
          {body.steps.map((s, i) => {
            const warn = lint.some((l) => l.stepId === s.id) || !(s.title[lang] ?? "").trim() || s.blockIds.length === 0;
            return (
              <button key={s.id} type="button" className="step-item" aria-current={view === "steps" && i === stepIdx ? "step" : undefined} onClick={() => { setPreviewStep(i); setView("steps"); }}>
                <span className="n">{i + 1}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="t">{(s.title[lang] ?? "").trim() || <i className="muted">{s.title.fr ? `FR : ${s.title.fr}` : "Sans titre"}</i>}</span>
                  <span className="meta">
                    {body.blocks.filter((b) => s.blockIds.includes(b.id)).slice(0, 6).map((b) => <span key={b.id} title={snippet(b)}>{ICON[b.type] ?? <AlignLeft size={14} />}</span>)}
                    {s.recap && <span className="tiny strong" style={{ color: "var(--accent)" }}>à retenir</span>}
                    {warn && <span className="warn-dot" title="À vérifier" />}
                  </span>
                </span>
              </button>
            );
          })}
          {canWrite && <button type="button" className="btn sm subtle" style={{ justifyContent: "flex-start", marginTop: 4 }} onClick={addStep} disabled={body.steps.length >= 30}><Plus size={15} />Ajouter une étape</button>}
          {orphans.length > 0 && (
            <button type="button" className="alert warn small" style={{ marginTop: 8, cursor: "pointer", textAlign: "left" }} onClick={() => setView("blocks")}>
              <TriangleAlert size={15} /><span>{orphans.length} bloc{orphans.length > 1 ? "s" : ""} dans aucune étape</span>
            </button>
          )}
        </nav>

        <div className="stage">
          {lint.filter((l) => l.stepId === null).map((l) => <div key={l.message} className="alert warn small"><TriangleAlert size={15} />{l.message}</div>)}
          <fieldset disabled={!canWrite} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }} className="stack">
            {view === "steps" && step && (
              <section className="card step-card appear" key={step.id} aria-label={`Étape ${stepIdx + 1}`}>
                <div className="step-card-head">
                  <span className="n">{stepIdx + 1}</span>
                  <input className="title-input" aria-label="Titre de l'étape" placeholder={lang !== "fr" && step.title.fr ? `FR : ${step.title.fr}` : "Titre de l'étape"} value={step.title[lang] ?? ""} maxLength={120}
                    onChange={(e) => setBody((x) => updateStep(x, stepIdx, { title: { ...step.title, [lang]: e.target.value } }))} />
                  <div className="row" style={{ gap: 2 }}>
                    <button type="button" className="icon-btn" aria-label="Monter l'étape" title="Monter l'étape" disabled={stepIdx === 0} onClick={() => moveStep(stepIdx, -1)}><ArrowUp size={16} /></button>
                    <button type="button" className="icon-btn" aria-label="Descendre l'étape" title="Descendre l'étape" disabled={stepIdx === body.steps.length - 1} onClick={() => moveStep(stepIdx, 1)}><ArrowDown size={16} /></button>
                    <button type="button" className="icon-btn" aria-label="Supprimer l'étape" title="Supprimer l'étape" onClick={() => removeStep(stepIdx)}><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="step-opts">
                  {!ops && <label className="check" title="La carte reviendra dans la révision du jour (répétition espacée)">
                    <input type="checkbox" className="switch" checked={!!step.recap} onChange={(e) => setBody((x) => updateStep(x, stepIdx, { recap: e.target.checked || undefined }))} /> Carte « à retenir »
                  </label>}
                  <label className="check">
                    Narration
                    <select className="select" style={{ width: 220, minHeight: 32, paddingTop: 4, paddingBottom: 4 }} value={step.audio?.assetId ?? ""} onChange={(e) => setBody((x) => updateStep(x, stepIdx, { audio: e.target.value ? { assetId: e.target.value, startSec: 0, endSec: 0 } : undefined }))}>
                      <option value="">Aucune</option>
                      {step.audio && !readyAudios.some((a) => a.id === step.audio!.assetId) && <option value={step.audio.assetId}>{step.audio.assetId} (non disponible)</option>}
                      {readyAudios.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                    </select>
                  </label>
                  <StepSliders body={body} i={stepIdx} onChange={setBody} />
                </div>
                {lint.filter((l) => l.stepId === step.id).map((l) => <div key={l.message} className="alert warn small" style={{ margin: "12px 18px 0" }}><TriangleAlert size={15} />{l.message}</div>)}
                <div className="step-blocks">
                  {stepBlocks.length === 0 && <Empty icon={<Layers size={20} />} title="Cette carte est vide.">Ajoutez un bloc ci-dessous ou reprenez un bloc d'une autre étape.</Empty>}
                  {stepBlocks.map((b, k) => (
                    <BlockEditor key={b.id} block={b} {...editorProps} onChange={(nb) => setBlockById(b.id, nb)}
                      onMove={(d) => moveInStep(b.id, d)} first={k === 0} last={k === stepBlocks.length - 1}
                      usedIn={(usedIn.get(b.id) ?? []).filter((n) => n !== stepIdx + 1)}
                      onDetach={() => toggleInStep(b.id, false)} onRemove={() => removeEverywhere(b)} />
                  ))}
                  {canWrite && <AddBar onAdd={onAddType} reuse={body.blocks.filter((b) => !step.blockIds.includes(b.id))} onReuse={(id) => toggleInStep(id, true)} full={body.blocks.length >= 60} />}
                </div>
              </section>
            )}
            {view === "steps" && !step && (
              <div className="card"><Empty icon={<Layers size={20} />} title="Aucune étape.">{canWrite ? "Ajoutez une étape dans le plan à gauche." : ""}</Empty></div>
            )}

            {view === "blocks" && (
              <section className="card stack appear" style={{ gap: 12 }}>
                <div className="section-title" style={{ marginBottom: 0 }}><h2>Blocs</h2><span className="muted small">{body.blocks.length} / 60 · l'ordre ici est l'ordre d'affichage dans chaque carte</span></div>
                {body.blocks.map((b, i) => (
                  <BlockEditor key={b.id} block={b} {...editorProps} onChange={(nb) => setBlockById(b.id, nb)}
                    onMove={(d) => setBody((x) => sortSteps({ ...x, blocks: move(x.blocks, i, d) }))} first={i === 0} last={i === body.blocks.length - 1}
                    usedIn={usedIn.get(b.id) ?? []} showOrphan onRemove={() => removeEverywhere(b)} />
                ))}
                {canWrite && <AddBar onAdd={onAddType} full={body.blocks.length >= 60} hint={step ? `ajouté à l'étape ${stepIdx + 1}` : undefined} />}
              </section>
            )}
          </fieldset>

        </div>

        {wide && <aside className="preview" aria-label="Aperçu élève">{preview}</aside>}
      </div>
      {canWrite && (
        <div className={`actionbar ${wide ? "beside-preview" : ""}`} role="region" aria-label="Enregistrement">
          <div className="state">
            {errors.length > 0
              ? <><span className="dot bad" /><span style={{ color: "var(--bad)" }}>{errors[0]}{errors.length > 1 ? ` (+${errors.length - 1})` : ""}</span></>
              : <><span className={`dot ${dirty ? "on" : "ok"}`} /><span className="dim">{dirty ? `Modifications non enregistrées · deviendra la version ${nextVersion}` : selected ? `Version ${selected.version} · aucune modification` : "Nouveau contenu"}</span></>}
          </div>
          {selected && !dirty && ["DRAFT", "AI_DRAFT", "REJECTED"].includes(selected.status) && (isMine || teacher?.roles.includes("EDITOR")) && (
            <button className="btn ghost" disabled={submitExisting.isPending} onClick={() => submitExisting.mutate(selected.id)}><Send size={15} />Soumettre la v{selected.version}</button>
          )}
          {dirty && <button className="btn subtle" disabled={save.isPending} onClick={() => { if (window.confirm("Annuler toutes les modifications ?")) { setBody(clone(base)); localDraft.clear(notion.id, lang); } }}><Undo2 size={15} />Annuler</button>}
          <button className="btn ghost" disabled={!dirty || errors.length > 0 || save.isPending} onClick={() => save.mutate(false)}><Save size={15} />Enregistrer le brouillon</button>
          <button className="btn" disabled={!dirty || errors.length > 0 || save.isPending} onClick={() => save.mutate(true)}><Send size={15} />{save.isPending ? "Envoi…" : "Soumettre en relecture"}</button>
        </div>
      )}
      {picking && (
        <Drawer title="Banque 3D" wide onClose={() => setPicking(false)} subtitle={`Choisissez le modèle à poser sur l'étape ${stepIdx + 1}`}>
          <BankBrowser mode="pick" onPick={(m) => { addBlock("scene3d", modelPatch(m)); setPicking(false); setView("steps"); }} />
        </Drawer>
      )}
      {!wide && previewOpen && <Drawer title="Aperçu élève" onClose={() => setPreviewOpen(false)}><div className="preview" style={{ position: "static" }}>{preview}</div></Drawer>}
    </div>
  );
}

function StepSliders({ body, i, onChange }: { body: ContentBody; i: number; onChange: (f: (x: ContentBody) => ContentBody) => void }) {
  const sliders = stepSliders(body, i);
  const s = body.steps[i];
  if (!s || sliders.length === 0) return null;
  return (
    <div className="row wrap" style={{ gap: 10, fontWeight: 600 }}>
      <span className="muted">Curseurs à cette étape :</span>
      {sliders.map((sl) => (
        <label key={sl.name} className="row" style={{ gap: 4 }}>
          <i>{sl.name}</i> =
          <input className="input" type="number" step={sl.step} min={sl.min} max={sl.max} style={{ width: 80, minHeight: 32, padding: "4px 8px" }} placeholder={String(sl.initial)} value={s.figureParams?.[sl.name] ?? ""}
            onChange={(e) => {
              const v = e.target.value === "" ? undefined : Number(e.target.value);
              const next = { ...s.figureParams };
              if (v === undefined || !Number.isFinite(v)) delete next[sl.name]; else next[sl.name] = Math.max(sl.min, Math.min(sl.max, v));
              onChange((x) => updateStep(x, i, { figureParams: Object.keys(next).length ? next : undefined }));
            }} />
        </label>
      ))}
    </div>
  );
}

function AddBar({ onAdd, reuse, onReuse, full, hint }: { onAdd: (t: EditableType) => void; reuse?: Block[]; onReuse?: (id: string) => void; full: boolean; hint?: string }) {
  return (
    <div className="add-bar">
      <span className="small muted strong" style={{ marginRight: 4 }}>Ajouter{hint ? ` (${hint})` : ""} :</span>
      {BLOCK_TYPES.map((t) => <button key={t.type} type="button" className="btn sm ghost" disabled={full} onClick={() => onAdd(t.type)}>{ICON[t.type]}{t.label}</button>)}
      {reuse && reuse.length > 0 && onReuse && (
        <select className="select" style={{ width: 240, minHeight: 30, paddingTop: 3, paddingBottom: 3, fontSize: 12.5 }} value="" aria-label="Reprendre un bloc d'une autre étape" onChange={(e) => { if (e.target.value) onReuse(e.target.value); }}>
          <option value="">Reprendre un bloc existant…</option>
          {reuse.map((b) => <option key={b.id} value={b.id}>{b.id} · {snippet(b, 50)}</option>)}
        </select>
      )}
    </div>
  );
}

function VersionBar({ versions, selectedId, onSelect }: { versions: NotionDetail["contents"]; selectedId: string | null; onSelect: (id: string) => void }) {
  if (versions.length === 0) return <div className="muted small">Aucune version enregistrée dans cette langue.</div>;
  return (
    <div>
      <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
        <span className="muted small" style={{ fontWeight: 700, marginRight: 4 }}>Versions</span>
        {versions.map((v) => (
          <button key={v.id} type="button" className={`chip ${v.id === selectedId ? "on" : ""}`} aria-pressed={v.id === selectedId} onClick={() => onSelect(v.id)} title={`${v.author ?? "IA"} · ${fmtDate(v.createdAt)}${v.validatedBy ? ` · relu par ${v.validatedBy}` : ""}`}>
            v{v.version} <Badge status={v.status} />
          </button>
        ))}
      </div>
    </div>
  );
}

function ReviewPanel({ contentId, disabled, onDone, review = api.reviewContent }: { contentId: string; disabled: boolean; onDone: () => Promise<void>; review?: (id: string, d: "VALIDATED" | "REJECTED", note?: string) => Promise<unknown> }) {
  const { toast } = useStudio();
  const [note, setNote] = useState("");
  const m = useMutation({
    mutationFn: async (d: "VALIDATED" | "REJECTED") => { await review(contentId, d, note.trim() || undefined); await onDone(); return d; },
    onSuccess: (d) => { toast(d === "VALIDATED" ? "Version validée : les élèves la reçoivent" : "Corrections demandées à l'auteur"); setNote(""); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  return (
    <div className="card stack appear" style={{ borderColor: "var(--amber-line)", background: "var(--amber-soft)" }}>
      <h2>Relecture</h2>
      <p className="muted small" style={{ margin: 0 }}>Vérifiez le contenu et l'aperçu étape par étape. Une version validée est publiée aux élèves.</p>
      <Field label="Note pour l'auteur (obligatoire pour demander des corrections)">
        <textarea className="textarea" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {disabled && <div className="alert warn">Vous avez modifié le contenu : annulez vos modifications pour relire la version soumise.</div>}
      <div className="row">
        <button className="btn danger" disabled={disabled || m.isPending || !note.trim()} onClick={() => m.mutate("REJECTED")}>Demander des corrections</button>
        <span className="spacer" />
        <button className="btn" disabled={disabled || m.isPending} onClick={() => m.mutate("VALIDATED")}>Valider et publier</button>
      </div>
    </div>
  );
}

function BlockEditor({ block, lang, videos, models, images, onChange, onMove, onRemove, onDetach, first, last, usedIn, showOrphan }: {
  block: Block; lang: string; videos: Asset[]; models: Asset[]; images: Asset[]; onChange: (b: Block) => void; onMove: (d: -1 | 1) => void;
  onRemove: () => void; onDetach?: () => void; first: boolean; last: boolean; usedIn: number[]; showOrphan?: boolean;
}) {
  const label = BLOCK_TYPES.find((t) => t.type === block.type)?.label ?? block.type;
  return (
    <div className="block">
      <div className="block-head">
        <span className="block-type">{ICON[block.type] ?? <AlignLeft size={14} />}{label}</span>
        <span className="mono muted tiny">{block.id}</span>
        {usedIn.length > 0 && <span className="tiny muted" title="Ce bloc est partagé : le modifier le change partout">{onDetach ? "aussi dans l'étape" : "étape"}{usedIn.length > 1 ? "s" : ""} {usedIn.join(", ")}</span>}
        {showOrphan && usedIn.length === 0 && <span className="badge b-IN_REVIEW">dans aucune étape</span>}
        <div className="block-tools">
          <button type="button" className="icon-btn" aria-label="Monter" title="Monter" disabled={first} onClick={() => onMove(-1)}><ArrowUp size={15} /></button>
          <button type="button" className="icon-btn" aria-label="Descendre" title="Descendre" disabled={last} onClick={() => onMove(1)}><ArrowDown size={15} /></button>
          {onDetach && <button type="button" className="icon-btn" aria-label="Retirer de cette étape" title="Retirer de cette étape (le bloc reste disponible)" onClick={onDetach}><Unlink size={15} /></button>}
          <button type="button" className="icon-btn" aria-label="Supprimer le bloc" title="Supprimer le bloc partout" onClick={onRemove}><Trash2 size={15} /></button>
        </div>
      </div>
      {(block.type === "text" || block.type === "mistake") && (
        <textarea className="textarea" maxLength={4000} lang={lang} placeholder={block.type === "mistake" ? "Erreur fréquente et comment l'éviter…" : "Texte de la leçon. **gras**, *italique*"} value={block.md} onChange={(e) => onChange({ ...block, md: e.target.value })} />
      )}
      {block.type === "formula" && (
        <div className="row">
          <input className="input mono" placeholder="f(x) = ax^2" maxLength={600} value={block.latex} onChange={(e) => onChange({ ...block, latex: e.target.value })} />
          <label className="row small" style={{ whiteSpace: "nowrap", fontWeight: 600 }}><input type="checkbox" checked={block.display} onChange={(e) => onChange({ ...block, display: e.target.checked })} /> centrée</label>
        </div>
      )}
      {block.type === "figure" && (
        <FigureEditor value={block.spec} lang={lang} onChange={(spec) => onChange({ ...block, assetId: block.assetId || "inline", spec: spec as unknown as Record<string, unknown>, params: {} })} />
      )}
      {block.type === "video" && (
        <div className="row" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "2 1 260px" }}>
            <Field label="Vidéo de la médiathèque">
              <select className="select" value={block.assetId} onChange={(e) => {
                const v = videos.find((x) => x.id === e.target.value);
                onChange({ ...block, assetId: e.target.value, ...(v?.video ? { durationSec: v.video.durationSec, sizeBytes: v.sizeBytes, kind: v.video.kind } : {}) });
              }}>
                <option value="">— Choisir une vidéo —</option>
                {block.assetId && !videos.some((v) => v.id === block.assetId) && <option value={block.assetId}>{block.assetId} (non disponible)</option>}
                {videos.map((v) => <option key={v.id} value={v.id}>{v.title} · {Math.round((v.video?.durationSec ?? 0) / 60)} min · {(v.sizeBytes / 1e6).toFixed(1)} Mo</option>)}
              </select>
            </Field>
          </div>
          <Field label="Type">
            <select className="select" value={block.kind} onChange={(e) => onChange({ ...block, kind: e.target.value as typeof block.kind })}>
              <option value="TEACHER_INTRO">Présentation par un prof</option>
              <option value="REAL_EXPERIMENT">Expérience réelle</option>
              <option value="EXAM_CORRECTION">Correction d'examen</option>
            </select>
          </Field>
          <Link to="/medias" target="_blank" rel="noopener" className="btn sm ghost" style={{ height: 36 }}>Ajouter une vidéo ↗</Link>
          {block.sizeBytes > 15e6 && <span className="alert warn small">Lourd pour un forfait mobile : visez moins de 15 Mo.</span>}
        </div>
      )}
      {block.type === "image" && <ImageEditor block={block} lang={lang} images={images} onChange={onChange} />}
      {block.type === "geometry" && <GeometryEditor value={block.spec} lang={lang} onChange={(spec) => onChange({ ...block, spec: spec as unknown as Record<string, unknown> })} />}
      {block.type === "quiz" && <QuizEditor block={block} onChange={onChange} />}
      {block.type === "solid3d" && <SolidEditor value={block.spec} lang={lang} onChange={(spec) => onChange({ ...block, spec: spec as unknown as Record<string, unknown> })} />}
      {block.type === "scene3d" && <ModelEditor block={block} lang={lang} models={models} images={images} onChange={(p) => onChange({ ...block, ...p })} />}
      {!["text", "mistake", "formula", "figure", "geometry", "image", "video", "solid3d", "scene3d", "quiz"].includes(block.type) && <div className="muted small">Ce type de bloc ({block.type}) n'est pas encore éditable ici ; il est conservé tel quel.</div>}
    </div>
  );
}

/** Une erreur dans l'aperçu (formule ou figure en cours de saisie) ne doit jamais faire perdre le travail en cours. */
class PreviewBoundary extends Component<{ resetKey: string; children: ReactNode }, { error: Error | null; key: string }> {
  state = { error: null as Error | null, key: this.props.resetKey };
  static getDerivedStateFromError(error: Error) { return { error }; }
  static getDerivedStateFromProps(p: { resetKey: string }, s: { error: Error | null; key: string }) {
    return p.resetKey !== s.key ? { error: null, key: p.resetKey } : null;
  }
  render() {
    return this.state.error ? <div className="alert warn" style={{ margin: 16 }}>Aperçu indisponible : {this.state.error.message}</div> : this.props.children;
  }
}

type QuizBlock = Extract<Block, { type: "quiz" }>;
/** Question rapide : énoncé, 2 à 4 choix, bonne réponse, explication courte affichée après la réponse. */
function QuizEditor({ block, onChange }: { block: QuizBlock; onChange: (b: Block) => void }) {
  const set = (p: Partial<QuizBlock>) => onChange({ ...block, ...p });
  const ids = ["a", "b", "c", "d"];
  return (
    <div className="stack" style={{ gap: 8 }}>
      <input className="input" placeholder="Question (une seule idée, une touche pour répondre)" maxLength={300} value={block.question} onChange={(e) => set({ question: e.target.value })} />
      {block.choices.map((c, i) => (
        <div key={c.id} className="row">
          <input type="radio" name={`ans-${block.id}`} checked={block.answerId === c.id} onChange={() => set({ answerId: c.id })} aria-label={`Bonne réponse : choix ${i + 1}`} />
          <input className="input" placeholder={`Choix ${i + 1}`} maxLength={120} value={c.label} onChange={(e) => set({ choices: block.choices.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)) })} />
          <button type="button" className="icon-btn" aria-label="Retirer le choix" disabled={block.choices.length <= 2}
            onClick={() => { const choices = block.choices.filter((x) => x.id !== c.id); set({ choices, answerId: block.answerId === c.id ? choices[0].id : block.answerId }); }}>✕</button>
        </div>
      ))}
      <div className="row">
        <button type="button" className="btn sm ghost" disabled={block.choices.length >= 4} onClick={() => set({ choices: [...block.choices, { id: ids.find((x) => !block.choices.some((c) => c.id === x))!, label: "" }] })}>+ Choix</button>
        <span className="muted small">Cochez la bonne réponse.</span>
      </div>
      <input className="input" placeholder="Explication affichée après la réponse (facultatif)" maxLength={400} value={block.explanation} onChange={(e) => set({ explanation: e.target.value })} />
    </div>
  );
}

/** Aucune version dans cette langue : l'IA peut rédiger un premier brouillon (version AI_DRAFT), à relire et corriger. */
function AiLessonCard({ lang, onDone, start: startDraft, get, description }: { lang: string; onDone: () => Promise<void>; start: (lang: string, instructions?: string) => Promise<Draft>; get: (id: string) => Promise<Draft>; description: string }) {
  const { toast } = useStudio();
  const [instructions, setInstructions] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const start = useMutation({
    mutationFn: () => startDraft(lang, instructions.trim() || undefined),
    onSuccess: (d) => setDraft(d),
    onError: (e) => toast(errorMessage(e), "err"),
  });
  useEffect(() => {
    if (!draft || draft.status !== "PENDING") return;
    let alive = true;
    const tick = async () => {
      try {
        const d = await get(draft.id);
        if (!alive) return;
        if (d.status === "READY") { setDraft(null); toast(`Brouillon rédigé par l'IA${d.warnings.length ? ` (${d.warnings.length} point${d.warnings.length > 1 ? "s" : ""} à vérifier)` : ""} : relisez-le avant de le soumettre`); await onDone(); return; }
        if (d.status === "FAILED") { setDraft(null); toast(d.error ?? "La rédaction a échoué", "err"); return; }
        setTimeout(tick, 2500);
      } catch (e) { if (alive) { setDraft(null); toast(errorMessage(e), "err"); } }
    };
    const t = setTimeout(tick, 2500);
    return () => { alive = false; clearTimeout(t); };
  }, [draft?.id, draft?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="card stack appear" style={{ gap: 10, borderColor: "var(--accent-line)" }}>
      <div className="row wrap" style={{ gap: 10 }}>
        <span className="icon-tile"><Sparkles size={18} /></span>
        <span style={{ flex: 1, minWidth: 240 }}><b>Rédiger un premier brouillon avec l'IA.</b><br /><span className="small muted">{description}</span></span>
      </div>
      {draft ? <span className="chip">rédaction en cours… une à deux minutes, vous pouvez quitter la page</span> : (
        <div className="row wrap" style={{ gap: 8 }}>
          <input className="input" style={{ flex: "1 1 260px" }} placeholder="Consignes (facultatif) : exemples à utiliser, ce qu'il faut éviter…" maxLength={1000} value={instructions} onChange={(e) => setInstructions(e.target.value)} aria-label="Consignes pour l'IA" />
          <button type="button" className="btn" disabled={start.isPending} onClick={() => start.mutate()}><Sparkles size={15} />Rédiger avec l'IA</button>
        </div>
      )}
    </div>
  );
}
